import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { collection, doc, setDoc, onSnapshot, addDoc, query, where, deleteDoc, getDocs, serverTimestamp } from 'firebase/firestore';

const GroupCall = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { groupId, groupName, groupMembers } = location.state || {};
  
  const currentUid = auth.currentUser?.uid;

  // States
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [remotePeers, setRemotePeers] = useState([]); // অন্য ইউজারদের ভিডিও স্ট্রিম
  const [localStreamObj, setLocalStreamObj] = useState(null);

  // Refs for WebRTC
  const localVideoRef = useRef(null);
  const localStreamRef = useRef(null);
  const peersRef = useRef({}); // Store RTCPeerConnections { uid: pc }

  const iceServers = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' }
    ]
  };

  useEffect(() => {
    if (!groupId || !currentUid) return;

    const startConnection = async () => {
      try {
        // ১. ক্যামেরা ও মাইক্রোফোন পারমিশন নেওয়া
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        localStreamRef.current = stream;
        setLocalStreamObj(stream);
        
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }

        // ২. ফায়ারবেসে নিজেকে 'active' মেম্বার হিসেবে যুক্ত করা
        const myParticipantRef = doc(db, `calls/${groupId}/participants/${currentUid}`);
        await setDoc(myParticipantRef, { joinedAt: serverTimestamp(), uid: currentUid });

        // ৩. সিগনালিং লিসেনার (অন্যদের অফার, আন্সার এবং ICE রিসিভ করা)
        listenForSignals(stream);

        // ৪. যারা আগে থেকেই কলে আছে, তাদেরকে অফার (Offer) পাঠানো
        initiateCallsToExistingUsers(stream);

      } catch (error) {
        window.nexoraAlert("ক্যামেরা বা মাইক্রোফোনের পারমিশন দেওয়া নেই!");
        handleEndCall();
      }
    };

    startConnection();

    // ক্লিনআপ: কল কাটলে ক্যামেরা অফ এবং ফায়ারবেস থেকে রিমুভ হওয়া
    return () => {
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
      Object.values(peersRef.current).forEach(pc => pc.close());
      deleteDoc(doc(db, `calls/${groupId}/participants/${currentUid}`)).catch(()=>{});
    };
  }, [groupId, currentUid]);

  // ==========================================
  // WebRTC Signaling Logic (Mesh Network)
  // ==========================================
  
  const createPeerConnection = (targetUid, stream) => {
    const pc = new RTCPeerConnection(iceServers);
    peersRef.current[targetUid] = pc;

    // নিজের ভিডিও ট্র‍্যাক পিসি-তে অ্যাড করা
    stream.getTracks().forEach(track => pc.addTrack(track, stream));

    // অন্য ইউজারের ভিডিও ট্র‍্যাক রিসিভ করলে স্টেটে সেভ করা
    pc.ontrack = (event) => {
      setRemotePeers(prev => {
        if (prev.find(p => p.uid === targetUid)) return prev; // আগেই থাকলে অ্যাড করবেনা
        return [...prev, { uid: targetUid, stream: event.streams[0] }];
      });
    };

    // ICE Candidate ফায়ারবেসে পাঠানো
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        addDoc(collection(db, `calls/${groupId}/signals`), {
          type: 'ice',
          from: currentUid,
          to: targetUid,
          candidate: event.candidate.toJSON(),
          timestamp: serverTimestamp()
        });
      }
    };

    // ইউজার লিভ নিলে ভিডিও রিমুভ করা
    pc.oniceconnectionstatechange = () => {
      if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed') {
        setRemotePeers(prev => prev.filter(p => p.uid !== targetUid));
      }
    };

    return pc;
  };

  const initiateCallsToExistingUsers = async (stream) => {
    const participantsSnap = await getDocs(collection(db, `calls/${groupId}/participants`));
    participantsSnap.forEach(async (docSnap) => {
      const targetUid = docSnap.id;
      if (targetUid !== currentUid) {
        // পিসি তৈরি এবং অফার পাঠানো
        const pc = createPeerConnection(targetUid, stream);
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        await addDoc(collection(db, `calls/${groupId}/signals`), {
          type: 'offer',
          from: currentUid,
          to: targetUid,
          sdp: { type: offer.type, sdp: offer.sdp },
          timestamp: serverTimestamp()
        });
      }
    });
  };

  const listenForSignals = (stream) => {
    const signalsRef = collection(db, `calls/${groupId}/signals`);
    const q = query(signalsRef, where('to', '==', currentUid));

    onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach(async (change) => {
        if (change.type === 'added') {
          const data = change.doc.data();
          const signalId = change.doc.id;
          const { type, from, sdp, candidate } = data;

          try {
            if (type === 'offer') {
              const pc = createPeerConnection(from, stream);
              await pc.setRemoteDescription(new RTCSessionDescription(sdp));
              const answer = await pc.createAnswer();
              await pc.setLocalDescription(answer);

              await addDoc(collection(db, `calls/${groupId}/signals`), {
                type: 'answer',
                from: currentUid,
                to: from,
                sdp: { type: answer.type, sdp: answer.sdp },
                timestamp: serverTimestamp()
              });

            } else if (type === 'answer') {
              const pc = peersRef.current[from];
              if (pc && pc.signalingState !== "stable") {
                await pc.setRemoteDescription(new RTCSessionDescription(sdp));
              }

            } else if (type === 'ice') {
              const pc = peersRef.current[from];
              if (pc && pc.remoteDescription) {
                await pc.addIceCandidate(new RTCIceCandidate(candidate));
              }
            }
            
            // সিগন্যাল কাজ হয়ে গেলে ডাটাবেস ক্লিয়ার করা (ফ্রি টায়ার বাঁচানোর জন্য)
            deleteDoc(doc(db, `calls/${groupId}/signals/${signalId}`)).catch(()=>{});
          } catch (e) {
            console.log("Signaling error:", e);
          }
        }
      });
    });
  };

  // ==========================================
  // Controls & UI Elements
  // ==========================================

  const toggleMute = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks()[0].enabled = isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getVideoTracks()[0].enabled = isVideoOff;
      setIsVideoOff(!isVideoOff);
    }
  };

  const handleEndCall = () => {
    navigate(-1);
  };

  // Rule 1: Layout flexbox 100dvh
  return (
    <div className="slide-in" style={{ height: '100dvh', display: 'flex', flexDirection: 'column', backgroundColor: '#ffffff', color: '#000' }}>
      
      {/* Header Area */}
      <div style={{ flex: '0 0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', zIndex: 10 }}>
        <button onClick={handleEndCall} style={styles.iconCircleBtn}>
          <span style={{ fontSize: '18px', color: '#000' }}>❮</span>
        </button>
        <div style={{ textAlign: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>{groupName || 'Group Call'}</h3>
          <span style={{ fontSize: '12px', color: '#00e676', fontWeight: '500' }}>End-to-End Encrypted</span>
        </div>
        <button style={styles.iconCircleBtn}>
          <span style={{ fontSize: '18px', color: '#000' }}>ℹ️</span>
        </button>
      </div>

      {/* Video Area */}
      <div style={{ flex: '1 1 auto', overflowY: 'auto', padding: '10px', display: 'flex', flexDirection: 'column', position: 'relative' }}>
        
        {remotePeers.length === 0 ? (
          // শুধুমাত্র নিজে থাকলে (Waiting Screen)
          <div style={{ flex: 1, position: 'relative', borderRadius: '20px', overflow: 'hidden', backgroundColor: '#000' }}>
            <video ref={localVideoRef} autoPlay playsInline muted style={styles.fullVideo} />
            <div style={styles.overlayText}>Waiting for others...</div>
            <div style={styles.nameBadge}>You</div>
          </div>
        ) : (
          // অন্য কেউ জয়েন করলে (Grid Screen)
          <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center', alignContent: 'center' }}>
            
            {/* Local PIP Video */}
            <div style={{ ...styles.gridCard, flex: '1 1 45%' }}>
              <video ref={localVideoRef} autoPlay playsInline muted style={styles.fullVideo} />
              <div style={styles.nameBadge}>You</div>
            </div>

            {/* Remote Videos */}
            {remotePeers.map(peer => (
              <div key={peer.uid} style={{ ...styles.gridCard, flex: '1 1 45%' }}>
                <video 
                  autoPlay 
                  playsInline 
                  ref={(ref) => { if (ref && ref.srcObject !== peer.stream) ref.srcObject = peer.stream; }} 
                  style={styles.fullVideo} 
                />
                <div style={styles.nameBadge}>User</div>
              </div>
            ))}
            
          </div>
        )}
      </div>

      {/* Bottom Controls Area */}
      <div style={{ flex: '0 0 auto', padding: '15px 20px', paddingBottom: '30px' }}>
        <div style={styles.floatingFooter}>
          <button style={styles.controlBtnGray}>
            <span style={{ fontWeight: 'bold', fontSize: '20px' }}>···</span>
          </button>
          
          <button onClick={toggleVideo} style={isVideoOff ? styles.controlBtnRed : styles.controlBtnGray}>
            📷
          </button>
          
          <button style={styles.controlBtnGray}>
            🔊
          </button>
          
          <button onClick={toggleMute} style={isMuted ? styles.controlBtnRed : styles.controlBtnGray}>
            🎤
          </button>
          
          <button onClick={handleEndCall} style={styles.endBtn}>
            ☎
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  iconCircleBtn: {
    width: '45px', height: '45px', borderRadius: '50%', border: 'none',
    backgroundColor: '#f2f2f2', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer'
  },
  floatingFooter: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    backgroundColor: '#f9f9f9', borderRadius: '40px', padding: '10px 20px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.05)'
  },
  controlBtnGray: {
    width: '50px', height: '50px', borderRadius: '50%', border: 'none',
    backgroundColor: '#ececec', display: 'flex', justifyContent: 'center', alignItems: 'center',
    fontSize: '20px', cursor: 'pointer'
  },
  controlBtnRed: {
    width: '50px', height: '50px', borderRadius: '50%', border: 'none',
    backgroundColor: '#ff4c4c', color: '#fff', display: 'flex', justifyContent: 'center', alignItems: 'center',
    fontSize: '20px', cursor: 'pointer'
  },
  endBtn: {
    width: '60px', height: '60px', borderRadius: '50%', border: 'none',
    backgroundColor: '#ff4c4c', color: '#fff', display: 'flex', justifyContent: 'center', alignItems: 'center',
    fontSize: '26px', cursor: 'pointer', boxShadow: '0 4px 10px rgba(255, 76, 76, 0.4)'
  },
  fullVideo: {
    width: '100%', height: '100%', objectFit: 'cover'
  },
  gridCard: {
    position: 'relative', backgroundColor: '#333', borderRadius: '16px', overflow: 'hidden', minHeight: '150px'
  },
  overlayText: {
    position: 'absolute', top: '20px', left: 0, right: 0, textAlign: 'center',
    color: '#fff', fontSize: '18px', fontWeight: 'bold', textShadow: '0px 2px 4px rgba(0,0,0,0.8)'
  },
  nameBadge: {
    position: 'absolute', bottom: '10px', left: '10px', backgroundColor: 'rgba(0,0,0,0.6)',
    color: '#fff', padding: '4px 10px', borderRadius: '8px', fontSize: '12px'
  }
};

export default GroupCall;